import { resolveProductFromPathname } from "../../i18n/routeLocale";
import { resolveRoutePath } from "../../i18n/routeGuards";
import { ROUTE_IDS } from "../../i18n/routeIds";
import {
  HOTEL_PROFILE_BACK_MISSING_CONTEXT_MESSAGE,
  resolveHotelBackToResultsNavigation,
  resolveStoredHotelResultsUrlForProfileNavigation,
  traceHotelProfileBackToResults,
  isValidHotelSearchResultsReturnUrl,
} from "./resolveHotelBackToResultsNavigation";

export {
  HOTEL_PROFILE_BACK_MISSING_CONTEXT_MESSAGE,
  HOTEL_SEARCH_CONTEXT_STORAGE_KEY,
  resolveHotelBackToResultsNavigation,
  hasHotelBackToResultsSearchContext,
  readPersistedHotelSearchContext,
  resolveStoredHotelResultsUrlForProfileNavigation,
  isValidHotelSearchResultsReturnUrl,
} from "./resolveHotelBackToResultsNavigation";

export const HOTEL_SEARCH_RESULTS_URL_SESSION_KEY = "lastHotelSearchResultsUrl";
export const HOTEL_SEARCH_RESULTS_SNAPSHOT_SESSION_KEY = "lastHotelSearchResultsSnapshot";

export function buildCurrentResultsUrl(location) {
  if (location?.pathname != null) {
    return `${location.pathname}${location.search || ""}${location.hash || ""}`;
  }
  if (typeof window !== "undefined") {
    return `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`;
  }
  return "";
}

export function readHotelSearchResultsSnapshot() {
  try {
    const raw = sessionStorage.getItem(HOTEL_SEARCH_RESULTS_SNAPSHOT_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function readHotelSearchResultsUrl() {
  try {
    return sessionStorage.getItem(HOTEL_SEARCH_RESULTS_URL_SESSION_KEY) || null;
  } catch {
    return null;
  }
}

export function captureHotelResultsScrollPosition() {
  if (typeof document === "undefined") {
    return { windowScrollY: 0, resultsContainerScrollTop: 0 };
  }
  const windowScrollY = typeof window !== "undefined" ? window.scrollY || 0 : 0;
  const container =
    document.querySelector(".searchResults-results") ||
    document.querySelector(".tw-overflow-y-scroll");
  return {
    windowScrollY,
    resultsContainerScrollTop: container?.scrollTop ?? 0,
  };
}

export function persistHotelSearchResultsSnapshot({
  fromResultsUrl,
  filtersState,
  scrollPosition,
  resultsCount,
  filteredResultsCount,
  searchTab,
  isSearched,
} = {}) {
  try {
    const candidateUrl = fromResultsUrl || buildCurrentResultsUrl();
    const url = isValidHotelSearchResultsReturnUrl(candidateUrl) ? candidateUrl : null;
    if (url) {
      sessionStorage.setItem(HOTEL_SEARCH_RESULTS_URL_SESSION_KEY, url);
    }
    const snapshot = {
      fromResultsUrl: url,
      filtersState: filtersState ?? null,
      scrollPosition: scrollPosition ?? captureHotelResultsScrollPosition(),
      resultsCount: resultsCount ?? 0,
      filteredResultsCount: filteredResultsCount ?? resultsCount ?? 0,
      searchTab: searchTab ?? null,
      isSearched: Boolean(isSearched),
      savedAt: Date.now(),
    };
    sessionStorage.setItem(
      HOTEL_SEARCH_RESULTS_SNAPSHOT_SESSION_KEY,
      JSON.stringify(snapshot)
    );
    return snapshot;
  } catch {
    return null;
  }
}

export function resolveHotelSearchEngineFallbackUrl(fromPathname) {
  const pathname =
    fromPathname ||
    (typeof window !== "undefined" ? window.location.pathname : "") ||
    "";
  const ctx = resolveProductFromPathname(pathname);
  const locale = ctx.locale || "he";

  const hotelSearch = resolveRoutePath(ROUTE_IDS.HOTEL_SEARCH, {
    locale,
    product: ctx.product,
    useLegacyAlias: ctx.isLegacyAlias !== false,
  });
  if (hotelSearch?.path) return hotelSearch.path;

  const searchOffer = resolveRoutePath(ROUTE_IDS.SEARCH_OFFER, {
    locale,
    product: ctx.product,
    useLegacyAlias: ctx.isLegacyAlias !== false,
  });
  return searchOffer?.path || "/p/dashboard/searchoffer";
}

/** @deprecated Prefer resolveHotelBackToResultsNavigation */
export function resolveHotelProfileBackNavigationTarget({
  locationState,
  fromPathname,
} = {}) {
  const resolved = resolveHotelBackToResultsNavigation({
    locationState,
    fromPathname,
  });
  const snapshot = readHotelSearchResultsSnapshot();
  return {
    navigateTarget: resolved.targetUrl,
    fromResultsUrl: resolved.trace?.previousResultsUrl ?? null,
    fallbackUsed: resolved.sourceUsed === "searchContextRoute",
    hasLocationState: Boolean(locationState?.fromResultsUrl),
    hasSessionSearchSnapshot: Boolean(snapshot),
    filtersSnapshot:
      locationState?.filtersSnapshot ?? snapshot?.filtersState ?? null,
    scrollPosition: snapshot?.scrollPosition ?? null,
    resultsCount: snapshot?.resultsCount ?? null,
    snapshot,
    canNavigate: resolved.canNavigate,
  };
}

export function navigateHotelProfileBackToResults(navigate, location, options = {}) {
  const {
    hotelId,
    isDynamicPackage = false,
    searchPhrase = null,
    searchTab = null,
    onBlocked = null,
  } = options;

  const currentUrl = buildCurrentResultsUrl(location);
  const resolved = resolveHotelBackToResultsNavigation({
    currentHotelId: hotelId,
    currentUrl,
    locationState: location?.state,
    fromPathname: location?.pathname,
    searchPhrase,
    searchTab,
    isDynamicPackage,
  });

  const actionTaken = resolved.canNavigate ? "navigate" : "blocked_show_error";

  traceHotelProfileBackToResults({
    ...resolved.trace,
    sourceUsed: resolved.sourceUsed,
    targetUrl: resolved.targetUrl,
    canNavigate: resolved.canNavigate,
    reason: resolved.reason,
    actionTaken,
    isDynamicPackage,
  });

  if (!resolved.canNavigate) {
    if (typeof onBlocked === "function") {
      onBlocked(resolved);
    }
    return resolved;
  }

  navigate(resolved.targetUrl, {
    replace: false,
    state: resolved.navigateState,
  });

  return resolved;
}

export function buildHotelProfileNavigateState({
  seed,
  location,
  filtersState,
  resultsCount,
  filteredResultsCount,
  searchTab,
  isSearched,
}) {
  const fromResultsUrl = resolveStoredHotelResultsUrlForProfileNavigation(location);
  if (fromResultsUrl) {
    persistHotelSearchResultsSnapshot({
      fromResultsUrl,
      filtersState,
      scrollPosition: captureHotelResultsScrollPosition(),
      resultsCount,
      filteredResultsCount,
      searchTab,
      isSearched,
    });
  }
  return { seed, fromResultsUrl: fromResultsUrl ?? undefined };
}

export function restoreHotelResultsScrollPosition(scrollPosition) {
  if (!scrollPosition || typeof window === "undefined") return;
  requestAnimationFrame(() => {
    if (scrollPosition.windowScrollY > 0) {
      window.scrollTo(0, scrollPosition.windowScrollY);
    }
    const container =
      document.querySelector(".searchResults-results") ||
      document.querySelector(".tw-overflow-y-scroll");
    if (container && scrollPosition.resultsContainerScrollTop > 0) {
      container.scrollTop = scrollPosition.resultsContainerScrollTop;
    }
  });
}

export function isHotelProfilePathname(pathname = "") {
  const p = String(pathname);
  return (
    p.includes("hotelprofile") ||
    p.includes("hotels/profile") ||
    p.includes("dynamic-package-profile")
  );
}

export function extractHotelIdFromProfilePathname(pathname = "") {
  const p = String(pathname);
  const legacyMatch = p.match(/\/hotelprofile\/([^/]+)/i);
  if (legacyMatch?.[1]) return legacyMatch[1];
  const intlMatch = p.match(/\/hotels\/profile\/([^/]+)/i);
  if (intlMatch?.[1]) return intlMatch[1];
  const dpMatch = p.match(/\/dynamic-package-profile\/([^/]+)/i);
  if (dpMatch?.[1]) return dpMatch[1];
  return null;
}

export { HOTEL_PROFILE_BACK_MISSING_CONTEXT_MESSAGE as HOTEL_PROFILE_BACK_BLOCKED_MESSAGE };
